import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Label,
  Pagination,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight, FaUserShield } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import { BsFillBarChartFill } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import TableLoading from "../TableLoading";
import { FaRegEdit, FaUsers } from "react-icons/fa";
import UserForm from "../forms/UserForm";
import Drawer from "../Drawer";
import { Tooltip } from "react-tooltip";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { LuFilter, LuFilterX } from "react-icons/lu";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const AdminUsersTable = ({ isActive = true }) => {
  const queryClient = useQueryClient();
  const { user } = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const onPageChange = (page) => setCurrentPage(page);
  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerEditUser, setIsDrawerEditUser] = useState(false);
  const [isDrawerOpenUser, setIsDrawerOpenUser] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const handleImpersonateUser = async (user) => {
    if (!user?.id) return;
    try {
      setImpersonatingId(user.id);

      // pega token do backend
      const { data } = await axios.get(`${ADMIN.GET_TOKEN}?userId=${user.id}`);
      const token = data?.data?.token || data?.token;
      if (!token) throw new Error("Token de impersonação não retornado.");

      // seta no localStorage da aba corrente
      localStorage.setItem("token", token);

      // redireciona para a home (ou dashboard)
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      alert(err?.message || "Erro ao personificar usuário.");
    } finally {
      setImpersonatingId(null);
    }
  };

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true); // mostra o conteúdo imediatamente
    } else {
      // espera a animação terminar antes de esconder
      const timeout = setTimeout(() => setRenderFilters(false), 500); // mesmo tempo da transition
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers"],
    async () => {
      try {
        const { data } = await axios.get(
          `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`
        );

        if (!data?.data?.data) {
          throw new Error("Erro ao buscar clientes");
        }

        let customers = data.data.data.map((customer) => ({
          label:
            customer.typeCustomer === "CPF"
              ? customer.name
              : customer.socialReason,
          value: customer.id,
        }));

        customers = [{ label: "Todos os clientes", value: 0 }, ...customers];

        return customers;
      } catch (error) {
        throw new Error(`Erro ao buscar clientes: ${error.message}`);
      }
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingListUsers,
    error: errorListUsers,
    data: listUsers,
  } = useQuery(
    [
      "listUsers",
      currentPage,
      selectedStatuses,
      startDate,
      endDate,
      selectedCustomer,
      nameParam,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_USERS}?page=${currentPage}&status=${selectedStatuses}&companyId=${selectedCustomer?.value}&startDate=${startDate}&endDate=${endDate}&search=${nameParam}`
      );

      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);

      return data.data.data;
    },
    { enabled: isActive }
  );

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  // const handleSearchName = (e) => {
  //   e.preventDefault();
  //   setNameParam(searchName);
  // };

  const handleEditCustomer = (user) => {
    setSelectedUser(user);
    setIsDrawerEditUser((prevState) => !prevState);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="primary text-white transition-all duration-300"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className={`transition-transform duration-300 `}>
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div
            className={`transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-md shadow-sm border border-gray-200 p-4 relative`}
          >
            {renderFilters && (
              <div className="transition-opacity duration-500 min-w-[200px]">
                {isLoadingCustomers && <DrawerLoading />}
                <div className="title-syncx">Filtro avançado</div>
                <div className="flex flex-col md:flex-row gap-4 md:gap-10">
                  <div className="mt-5 min-w-[250px]">
                    <AutoCompleteInput
                      options={listCustomers}
                      label="Cliente"
                      onChange={(customer) => setSelectedCustomer(customer)}
                      value={selectedCustomer?.value}
                      isLoading={isLoadingCustomers}
                    />
                    {errorListCustomers && (
                      <Alert color="failure">
                        Ocorreu um erro ao buscar clientes
                      </Alert>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold mt-1 text-sm mb-2">
                      Criado em
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate))) {
                            setEndDate(e.target.value);
                          }
                        }}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                      />
                      <div className="text-xs">Até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                        }}
                        min={startDate}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                      />
                    </div>
                  </div>
                  <div className="mt-1 text-sm">
                    <div className="font-semibold">Status</div>
                    <ul className="mt-3 flex flex-col gap-2 md:flex-row md:gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="ativo"
                          checked={selectedStatuses.includes(1)}
                          onChange={() =>
                            handleCheckboxChange(1, setSelectedStatuses)
                          }
                        />
                        <Label htmlFor="ativo" className="flex">
                          Ativo
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="Bloqueado"
                          checked={selectedStatuses.includes(0)} // 0 = Falha
                          onChange={() =>
                            handleCheckboxChange(0, setSelectedStatuses)
                          }
                        />
                        <Label htmlFor="Bloqueado" className="flex">
                          Bloqueado
                        </Label>
                      </div>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex-1">
          <div className="title-syncx mt-3 mb-4">Resultado da busca</div>
          <div className="overflow-x-scroll">
            <div className="flex gap-3 pb-3" style={{ minWidth: "500px" }}>
              <Card
                className="w-full bg-gray-700 relative"
                style={{ minWidth: "150px" }}
              >
                <div className="text-sm md:text-md lg:text-lg text-white">
                  Total de usuários:
                </div>
                <div className="text-2xl text-white font-semibold flex gap-2 items-center">
                  {!isLoadingListUsers ? (
                    `${totalCount}`
                  ) : (
                    <div
                      role="status"
                      className="max-w-sm animate-pulse inline"
                    >
                      <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                    </div>
                  )}
                </div>
                <BsFillBarChartFill className="absolute bottom-0 right-2 text-white text-9xl opacity-10" />
              </Card>
              <Card className="w-full bg-green-700 relative overflow-hidden">
                <div className="text-sm md:text-md lg:text-lg text-white">
                  Total de usuários ativos:
                </div>
                <div className="text-2xl text-white font-semibold">
                  {!isLoadingListUsers ? (
                    `${totalCount}`
                  ) : (
                    <div
                      role="status"
                      className="max-w-sm animate-pulse inline"
                    >
                      <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                    </div>
                  )}
                </div>
                <FaUsers className="absolute -bottom-3 right-2 text-white text-9xl opacity-10" />
              </Card>
            </div>
          </div>

          <form
            className="flex gap-1 mt-5 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-300"
            onSubmit={handleSearchName}
          >
            <input
              type="text"
              placeholder="Busque por nome do usuário..."
              className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-300 placeholder:text-gray-700"
              onChange={(e) => setSearchName(e.target.value)}
              value={searchName}
            />
            <Button
              size="sm"
              className="flex items-center bg-secondary w-20"
              type="submit"
            >
              <IoMdSearch />
            </Button>
          </form>

          <small
            className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
            style={{ fontSize: 10 }}
          >
            <span>
              Deslize para o lado para visualizar todas as informações
            </span>{" "}
            <FaArrowsLeftRight />
          </small>
          {listUsers?.length > 0 || isLoadingListUsers ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow mt-0 md:mt-5">
              <div className="overflow-x-auto">
                <Table hoverable striped className="text-xs xl:text-sm">
                  <Table.Head className="sticky top-0 bg-gray-100">
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Código de acesso
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Usuário
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      E-mail
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Cliente
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Ações
                    </Table.HeadCell>
                  </Table.Head>

                  <Table.Body className="divide-y">
                    {!isLoadingListUsers ? (
                      (listUsers?.length ?? 0) > 0 ? (
                        listUsers.map((u, index) => {
                          const tooltipId = `tooltip-edit-user-${
                            u?.id ?? index
                          }`;
                          const customerName =
                            u?.Company?.typeCustomer === "CPF"
                              ? u?.Company?.name
                              : u?.Company?.socialReason;

                          return (
                            <Table.Row
                              key={index}
                              className="bg-white focus-within:bg-indigo-50/40 hover:bg-indigo-50/40"
                            >
                              <Table.Cell className="text-center py-2">
                                {u.accessCode ? (
                                  <span className="inline-flex items-center gap-2">
                                    <code className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-800">
                                      {u.accessCode}
                                    </code>
                                    {/* opcional: botão copiar */}
                                    <button
                                      type="button"
                                      className="rounded px-1.5 py-0.5 text-[11px] border border-gray-200 hover:bg-gray-100"
                                      onClick={() =>
                                        navigator.clipboard.writeText(
                                          u.accessCode
                                        )
                                      }
                                      aria-label="Copiar código"
                                      title="Copiar"
                                    >
                                      Copiar
                                    </button>
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </Table.Cell>

                              <Table.Cell className="text-center py-2 font-medium text-gray-900">
                                <span
                                  className="truncate inline-block max-w-[180px] md:max-w-[240px]"
                                  title={u.name}
                                >
                                  {u.name}
                                </span>
                              </Table.Cell>

                              <Table.Cell className="text-center py-2">
                                {u.email ? (
                                  <a
                                    href={`mailto:${u.email}`}
                                    className="text-gray-800 underline-offset-2 hover:underline"
                                    title={u.email}
                                  >
                                    <span className="truncate inline-block max-w-[200px] md:max-w-[260px]">
                                      {u.email}
                                    </span>
                                  </a>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </Table.Cell>

                              <Table.Cell className="text-center py-2 font-medium text-gray-900">
                                <span
                                  className="truncate inline-block max-w-[200px] md:max-w-[260px]"
                                  title={customerName}
                                >
                                  {customerName || "-"}
                                </span>
                              </Table.Cell>

                              <Table.Cell className="text-center py-2">
                                <div className="flex gap-2 justify-center items-center flex-wrap">
                                  {/* Tooltip Editar */}
                                  <Tooltip
                                    id={tooltipId}
                                    content="Editar usuário"
                                  />
                                  <FaRegEdit
                                    data-tooltip-id={tooltipId}
                                    fontSize="18"
                                    onClick={() => handleEditCustomer(u)}
                                    className="text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                                    aria-label={`Editar ${
                                      u?.name ?? "usuário"
                                    }`}
                                  />

                                  {u?.id != 1 && (
                                    <>
                                      <Tooltip
                                        id={`imp-${u?.id ?? index}`}
                                        content="Personificar usuário"
                                      />
                                      <button
                                        type="button"
                                        data-tooltip-id={`imp-${
                                          u?.id ?? index
                                        }`}
                                        onClick={() => handleImpersonateUser(u)}
                                        className="rounded px-2 py-1 text-[12px] border border-gray-200 hover:bg-gray-100 flex items-center gap-1"
                                        aria-label={`Personificar ${
                                          u?.name ?? "usuário"
                                        }`}
                                        disabled={impersonatingId === u?.id}
                                      >
                                        {impersonatingId === u?.id ? (
                                          <AiOutlineLoading3Quarters className="animate-spin" />
                                        ) : (
                                          <FaUserShield />
                                        )}
                                        <span>Personificar</span>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })
                      ) : null
                    ) : (
                      <TableLoading cols={5} />
                    )}
                  </Table.Body>
                </Table>
              </div>
            </div>
          ) : (
            <EmptyTable icon="search_off">
              Nenhum registro encontrado com esses filtros
            </EmptyTable>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </div>

        <Drawer
          title={`Editar cliente`}
          onClose={setIsDrawerEditUser}
          isOpen={isDrawerEditUser}
          className={"w-full md:w-4/6 lg:w-2/6"}
        >
          <UserForm
            user={selectedUser}
            onClose={setIsDrawerEditUser}
            isOpen={isDrawerEditUser}
            type={"EDIT"}
            fetchUsers={() => {
              queryClient.invalidateQueries(["listUsers"]);
            }}
          />
        </Drawer>
      </div>
    </div>
  );
};

export default AdminUsersTable;
